import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { trpc } from '@/providers/trpc';
import {
  Workflow, ListChecks, FlaskConical, Database, Trash2, Save, Play,
  ChevronRight, CheckCircle2, Clock, AlertCircle, Loader2, ArrowRight,
  Droplets, Beaker, Gauge, Zap, Wind, Layers, Cog, ShieldCheck,
  TrendingUp, Info, ArrowDown, Server, WifiOff, RefreshCw,
  Microscope, ArrowUp, ArrowDown as ArrowDownIcon, Lightbulb,
  X, FileText, Users, AlertTriangle, ChevronDown, ChevronUp,
  Printer, CheckSquare, XSquare, Minus, Undo2, Redo2, Home, Building2,
  BarChart3, FlaskRound, Activity, Shield, Globe, Atom, BookOpen,
  TrendingDown, ArrowDownRight, Beaker as BeakerIcon, Target,
} from 'lucide-react';
import { simulateTrain, type TrainSimulation, type ComplianceGap, CAPEX_LABELS, fmtWQParam, WQ_META } from '@/lib/processTrainSimulator';
import type { StandardCode } from '@/types/standards';
import { StickyActionBar } from '@/components/StickyActionBar';
import BreadcrumbNav, { type BreadcrumbItem } from '@/components/BreadcrumbNav';
import PageHeader from '@/components/PageHeader';
import {
  getAllLocalSamples,
  getLocalProcessTrains,
  saveLocalProcessTrain,
  updateLocalProcessTrain,
  deleteLocalProcessTrain,
} from '@/lib/hybridStorage';
import { apiListTrains as phpApiListTrains } from '@/lib/phpApi';
import ProcessTrainValidationReport from '@/components/ProcessTrainValidationReport';
import { useProposal } from '@/context/ProposalContext';
import { useSession } from '@/context/SessionContext';
import { useAuth } from '@/hooks/useAuth';
import { getRetentionPolicy, isWithinRetention, retentionToastMessage, adminOverridePrompt } from '@/lib/retention';

// ─── Equipment Definitions ───────────────────────────────────────────────────
interface EquipmentItem {
  key: string;
  label: string;
  shortLabel: string;
  icon: React.ElementType;
  category: 'pretreatment' | 'primary' | 'secondary' | 'tertiary' | 'sludge';
  description: string;
}

const EQUIPMENT: EquipmentItem[] = [
  { key: 'screening', label: 'Screening', shortLabel: 'Screen', icon: Cog, category: 'pretreatment', description: 'Coarse & fine screens for large solids removal' },
  { key: 'eq', label: 'Equalization Tank', shortLabel: 'EQ Tank', icon: Gauge, category: 'pretreatment', description: 'Flow & load equalization buffer' },
  { key: 'ph', label: 'pH Adjustment', shortLabel: 'pH Adj.', icon: FlaskConical, category: 'pretreatment', description: 'Chemical pH correction unit' },
  { key: 'buffer', label: 'Buffer Tank', shortLabel: 'Buffer', icon: Gauge, category: 'pretreatment', description: 'Surge protection & process buffer' },
  { key: 'daf', label: 'DAF Unit', shortLabel: 'DAF', icon: Droplets, category: 'primary', description: 'Dissolved Air Flotation for FOG & TSS removal' },
  { key: 'lamella', label: 'Lamella Clarifier', shortLabel: 'Lamella', icon: Layers, category: 'primary', description: 'Inclined plate settler for solids separation' },
  { key: 'aeration', label: 'Aeration Tank', shortLabel: 'Aeration', icon: Wind, category: 'secondary', description: 'Biological aerobic treatment reactor' },
  { key: 'secondary', label: 'Secondary Clarifier', shortLabel: '2° Clarif.', icon: Beaker, category: 'secondary', description: 'Biomass separation after biological treatment' },
  { key: 'multimedia', label: 'Multimedia Filter', shortLabel: 'MMF', icon: Layers, category: 'tertiary', description: 'Sand/anthracite media filtration polishing' },
  { key: 'gac', label: 'GAC Filter', shortLabel: 'GAC', icon: Zap, category: 'tertiary', description: 'Granular Activated Carbon final polishing' },
  { key: 'sludge', label: 'Sludge Holding', shortLabel: 'Sludge', icon: ArrowDown, category: 'sludge', description: 'Sludge collection and holding tank' },
];

const CAT_LABELS: Record<string, string> = {
  pretreatment: 'Pre-Treatment',
  primary: 'Primary Treatment',
  secondary: 'Secondary Treatment',
  tertiary: 'Tertiary / Polishing',
  sludge: 'Sludge Management',
};

const CAT_COLORS: Record<string, { border: string; bg: string; text: string; dot: string }> = {
  pretreatment: { border: 'border-cyan-500/30', bg: 'bg-cyan-500/10', text: 'text-cyan-400', dot: 'bg-cyan-400' },
  primary:      { border: 'border-amber-500/30', bg: 'bg-amber-500/10', text: 'text-amber-400', dot: 'bg-amber-400' },
  secondary:    { border: 'border-emerald-500/30', bg: 'bg-emerald-500/10', text: 'text-emerald-400', dot: 'bg-emerald-400' },
  tertiary:     { border: 'border-sky-500/30', bg: 'bg-sky-500/10', text: 'text-sky-400', dot: 'bg-sky-400' },
  sludge:       { border: 'border-rose-500/30', bg: 'bg-rose-500/10', text: 'text-rose-400', dot: 'bg-rose-400' },
};

// ─── Equipment Adequacy Check ─────────────────────────────────────────────────
interface AdequacyResult {
  key: string;
  label: string;
  adequate: 'pass' | 'warning' | 'fail';
  message: string;
  recommendation?: string;
}

function checkEquipmentAdequacy(equipmentKeys: string[], sampleParams: Record<string, number>): AdequacyResult[] {
  const p = (k: string) => sampleParams[k] ?? sampleParams[k.toUpperCase()] ?? 0;
  const tss = p('tss'); const bod = p('bod'); const cod = p('cod');
  const og = p('og') || p('oil') || p('oil_grease'); const ph = p('ph') || 7;
  const flow = p('flow') || p('flowRate') || 0;

  return equipmentKeys.map(key => {
    const eq = EQUIPMENT.find(e => e.key === key)!;
    let adequate: 'pass' | 'warning' | 'fail' = 'pass';
    let message = '';
    let recommendation: string | undefined;

    switch (key) {
      case 'screening':
        if (tss > 500) { adequate = 'pass'; message = `TSS ${tss} mg/L — coarse solids warranted. Screening essential.`; }
        else if (tss > 100) { adequate = 'warning'; message = `TSS ${tss} mg/L — fine screening adequate. Monitor blinding.`; }
        else { adequate = 'warning'; message = `TSS ${tss} mg/L — screening may be oversized. Verify influent solids.`; }
        break;

      case 'eq':
        adequate = 'pass'; message = 'Equalization is universally beneficial for flow and load balancing.';
        if (flow < 5) recommendation = 'Low flow — compact EQ tank may suffice.';
        break;

      case 'ph':
        if (ph < 5.5 || ph > 10) { adequate = 'pass'; message = `pH ${ph} — pH adjustment critical. Outside biological operating range.`; }
        else if (ph < 6.5 || ph > 9) { adequate = 'warning'; message = `pH ${ph} — borderline for biological treatment. pH adjustment recommended.`; }
        else { adequate = 'warning'; message = `pH ${ph} — within acceptable range. pH adjustment may be precautionary.`; }
        break;

      case 'buffer':
        adequate = 'pass'; message = 'Buffer tank provides surge protection and process stability.';
        break;

      case 'daf':
        if (og > 50 || tss > 200) { adequate = 'pass'; message = `O&G ${og} mg/L, TSS ${tss} mg/L — DAF highly effective. Strong FOG/solids load.`; }
        else if (og > 20 || tss > 80) { adequate = 'pass'; message = `O&G ${og} mg/L, TSS ${tss} mg/L — DAF suitable. Moderate FOG/solids present.`; }
        else if (og > 0 || tss > 30) { adequate = 'warning'; message = `O&G ${og} mg/L, TSS ${tss} mg/L — DAF may be oversized. Consider cost-benefit.`; recommendation = 'Verify FOG data with detailed characterisation.'; }
        else { adequate = 'fail'; message = `Very low FOG/TSS detected. DAF likely unnecessary for this influent.`; recommendation = 'Consider simpler clarification or direct biological treatment.'; }
        break;

      case 'lamella':
        if (tss > 100 && tss < 1000) { adequate = 'pass'; message = `TSS ${tss} mg/L — within optimal lamella clarifier range (100–1000 mg/L).`; }
        else if (tss >= 1000) { adequate = 'warning'; message = `TSS ${tss} mg/L — very high solids may cause lamella fouling.`; recommendation = 'Consider pre-screening or thickening ahead of lamella.'; }
        else { adequate = 'warning'; message = `TSS ${tss} mg/L — low solids load. Lamella may be underutilised.`; }
        break;

      case 'aeration':
        if (bod > 150 || cod > 400) { adequate = 'pass'; message = `BOD ${bod} mg/L, COD ${cod} mg/L — biological treatment highly warranted.`; }
        else if (bod > 50 || cod > 150) { adequate = 'pass'; message = `BOD ${bod} mg/L, COD ${cod} mg/L — biological treatment appropriate.`; }
        else if (bod > 20 || cod > 60) { adequate = 'warning'; message = `BOD ${bod} mg/L — marginal biological load. Assess economic viability.`; recommendation = 'Consider physical-chemical alternatives if BOD < 50 mg/L.'; }
        else { adequate = 'fail'; message = `Very low BOD/COD. Biological treatment not recommended.`; recommendation = 'Use physical-chemical treatment. Biological biomass will struggle to establish.'; }
        break;

      case 'secondary':
        if (equipmentKeys.includes('aeration')) { adequate = 'pass'; message = 'Secondary clarifier correctly placed after Aeration Tank. Biomass separation confirmed.'; }
        else { adequate = 'fail'; message = 'Secondary Clarifier requires an Aeration Tank in the train.'; recommendation = 'Add Aeration Tank before Secondary Clarifier.'; }
        break;

      case 'multimedia':
        if (tss < 20) { adequate = 'pass'; message = `TSS ${tss} mg/L — low solids ideal for multimedia filter polishing.`; }
        else if (tss < 80) { adequate = 'warning'; message = `TSS ${tss} mg/L — multimedia filter will need frequent backwash.`; recommendation = 'Ensure upstream treatment reduces TSS < 20 mg/L ahead of MMF.'; }
        else { adequate = 'fail'; message = `TSS ${tss} mg/L — too high for direct multimedia filtration. Upstream treatment needed first.`; recommendation = 'DAF or secondary clarification must precede multimedia filter.'; }
        break;

      case 'gac':
        if (cod < 60 && tss < 10) { adequate = 'pass'; message = 'Low residual COD and TSS — GAC ideal for final trace organics polishing.'; }
        else if (cod < 150) { adequate = 'warning'; message = `COD ${cod} mg/L — GAC will work but bed life will be shorter.`; recommendation = 'Pre-treat to COD < 60 mg/L before GAC to maximise carbon life.'; }
        else { adequate = 'fail'; message = `COD ${cod} mg/L too high for GAC. Carbon will exhaust rapidly.`; recommendation = 'Ensure secondary biological treatment reduces COD before GAC stage.'; }
        break;

      case 'sludge':
        const generates = equipmentKeys.some(k => ['daf', 'lamella', 'aeration', 'secondary'].includes(k));
        if (generates) { adequate = 'pass'; message = 'Sludge holding required — primary/secondary stages generate significant sludge.'; }
        else { adequate = 'warning'; message = 'No major sludge-generating stages detected. Sludge holding may be precautionary.'; }
        break;

      default:
        adequate = 'pass'; message = 'Equipment adequacy check not available.';
    }

    return { key, label: eq?.label || key, adequate, message, recommendation };
  });
}

// ─── Sequence Validation ─────────────────────────────────────────────────────
interface TrainValidation { errors: string[]; warnings: string[]; suggestions: string[]; }

function validateTrainSequence(keys: string[]): TrainValidation {
  const errors: string[] = []; const warnings: string[] = []; const suggestions: string[] = [];
  const idx = (k: string) => keys.indexOf(k);
  const has = (k: string) => keys.includes(k);
  const before = (a: string, b: string) => idx(a) !== -1 && idx(b) !== -1 && idx(a) < idx(b);
  const after = (a: string, b: string) => idx(a) !== -1 && idx(b) !== -1 && idx(a) > idx(b);

  if (before('gac', 'multimedia')) errors.push('GAC Filter must come AFTER Multimedia Filter.');
  if (before('secondary', 'aeration')) errors.push('Secondary Clarifier must come AFTER Aeration Tank.');
  if (before('multimedia', 'secondary')) errors.push('Multimedia Filter must come AFTER Secondary Clarifier.');
  if (before('gac', 'secondary')) errors.push('GAC Filter must come AFTER Secondary Clarifier.');
  if (before('gac', 'aeration')) errors.push('GAC Filter must come AFTER Aeration Tank.');
  if (before('multimedia', 'aeration')) errors.push('Multimedia Filter must come AFTER Aeration Tank.');

  if (after('aeration', 'daf') || after('aeration', 'lamella')) warnings.push('Aeration before primary separation — for high-strength industrial water, remove FOG/TSS first.');
  if (after('ph', 'aeration')) warnings.push('pH Adjustment after Aeration — pH should be stabilised before biological treatment.');
  if (has('secondary') && !has('aeration')) warnings.push('Secondary Clarifier without Aeration Tank is unusual.');
  if (before('sludge', 'daf') || before('sludge', 'aeration')) warnings.push('Sludge Holding placed too early in train sequence.');
  if (has('lamella') && has('daf')) warnings.push('Both DAF and Lamella selected — usually one primary stage suffices.');

  if (!has('eq') && !has('buffer')) suggestions.push('Consider Equalization or Buffer Tank for flow balancing.');
  if (has('aeration') && !has('daf') && !has('lamella')) suggestions.push('For high-strength industrial wastewater, add primary separation (DAF/Lamella) before biological treatment.');
  if ((has('daf') || has('lamella')) && !has('sludge')) suggestions.push('Primary separation generates significant sludge — add Sludge Holding.');
  if (has('secondary') && !has('multimedia') && !has('gac')) suggestions.push('Consider tertiary polishing (MMF or GAC) for stringent effluent quality targets.');

  return { errors, warnings, suggestions };
}

// ─── Validation Wizard Modal ─────────────────────────────────────────────────
interface ValidationWizardProps {
  equipmentKeys: string[];
  samples: any[];
  preSelectedSampleId: number | null;
  activeStandard: StandardCode;
  onClose: () => void;
  onSaveResult: (result: AdequacyResult[], sampleId: number | null, simulation?: TrainSimulation) => void;
}

const WIZARD_STEP_LABELS = ['Start', 'Sample', 'Compat.', 'Analysis', 'Results', 'Compliance', 'Save'];
const WIZARD_STEPS = 7;

function ValidationWizard({ equipmentKeys, samples, preSelectedSampleId, activeStandard, onClose, onSaveResult }: ValidationWizardProps) {
  const [step, setStep] = useState<1|2|3|4|5|6|7>(1);
  const [selectedSampleId, setSelectedSampleId] = useState<number | null>(preSelectedSampleId);
  const [results, setResults] = useState<AdequacyResult[]>([]);
  const [compatCheck, setCompatCheck] = useState<TrainValidation | null>(null);
  const [running, setRunning] = useState(false);
  const [simRunning, setSimRunning] = useState(false);
  const [simulation, setSimulation] = useState<TrainSimulation | null>(null);
  const [simStandard, setSimStandard] = useState<StandardCode>(activeStandard);
  const [expandedGap, setExpandedGap] = useState<string | null>(null);
  const [expandedStage, setExpandedStage] = useState<number | null>(null);

  const selectedSample = samples.find(s => s.id === selectedSampleId);
  const passCount = results.filter(r => r.adequate === 'pass').length;
  const warnCount = results.filter(r => r.adequate === 'warning').length;
  const failCount = results.filter(r => r.adequate === 'fail').length;

  const runCompatCheck = () => {
    const check = validateTrainSequence(equipmentKeys);
    setCompatCheck(check);
    setStep(3);
  };

  const runAdequacyCheck = () => {
    if (!selectedSample) return;
    setRunning(true);
    setStep(4);
    setTimeout(() => {
      const res = checkEquipmentAdequacy(equipmentKeys, selectedSample.params || {});
      setResults(res);
      setRunning(false);
      setStep(5);
    }, 1600);
  };

  const runComplianceSimulation = () => {
    if (!selectedSample) return;
    setSimRunning(true);
    setStep(6);
    setTimeout(() => {
      const sim = simulateTrain(selectedSample.params || {}, equipmentKeys, simStandard);
      setSimulation(sim);
      setSimRunning(false);
    }, 2000);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 pt-8 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl overflow-hidden rb-section"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-emerald-500/10 to-transparent">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            <div>
              <h2 className="text-sm font-bold text-slate-100">Process Validation Wizard</h2>
              <p className="text-[10px] text-slate-500">Step {step} of 5 — {WIZARD_STEP_LABELS[step - 1]}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800/50"><X className="w-4 h-4" /></button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center px-4 py-3 gap-1 border-b border-slate-800/50 overflow-x-auto scrollbar-hide">
          {[1, 2, 3, 4, 5, 6, 7].map((s) => (
            <div key={s} className="flex items-center gap-1 shrink-0">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold border transition-all ${
                s < step ? 'bg-emerald-500 border-emerald-500 text-white'
                : s === step ? (s === 6 ? 'bg-violet-500/20 border-violet-500 text-violet-400' : 'bg-emerald-500/20 border-emerald-500 text-emerald-400')
                : 'bg-slate-800 border-slate-700 text-slate-500'
              }`}>{s < step ? <CheckCircle2 className="w-3 h-3" /> : s}</div>
              <span className={`text-[9px] font-medium ${s === step ? 'text-slate-200' : 'text-slate-500'}`}>{WIZARD_STEP_LABELS[s - 1]}</span>
              {s < 7 && <ChevronRight className="w-2.5 h-2.5 text-slate-700" />}
            </div>
          ))}
        </div>

        <div className="p-6">

          {/* Step 1: Start — overview */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/15">
                <ShieldCheck className="w-8 h-8 text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-slate-200">Ready to validate your process train</p>
                  <p className="text-[11px] text-slate-400 mt-1">This wizard performs a 5-step engineering assessment of your selected equipment against influent sample data.</p>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Equipment selected ({equipmentKeys.length} units)</p>
                <div className="flex flex-wrap gap-1.5">
                  {equipmentKeys.map(key => {
                    const eq = EQUIPMENT.find(e => e.key === key);
                    const colors = CAT_COLORS[eq?.category || 'pretreatment'];
                    return (
                      <span key={key} className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${colors.bg} ${colors.border} ${colors.text}`}>
                        {eq?.shortLabel || key}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-1.5">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Validation checks</p>
                {[
                  { icon: FlaskConical, color: 'text-sky-400',     label: 'Choose influent sample to validate against' },
                  { icon: AlertCircle,  color: 'text-amber-400',   label: 'Equipment compatibility & sequence check (blocking)' },
                  { icon: ShieldCheck,  color: 'text-emerald-400', label: 'Science-based adequacy analysis per unit' },
                  { icon: Save,         color: 'text-violet-400',  label: 'Save validated process train to record' },
                ].map(({ icon: Icon, color, label }) => (
                  <div key={label} className="flex items-center gap-2 text-[10px] text-slate-400">
                    <Icon className={`w-3.5 h-3.5 ${color} shrink-0`} />{label}
                  </div>
                ))}
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-400 text-xs font-bold hover:bg-slate-800/50">Cancel</button>
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 py-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs font-bold hover:bg-emerald-500/30 flex items-center justify-center gap-2"
                >
                  Begin Validation <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Choose Sample */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-xs text-slate-400">Select an influent sample to validate equipment selection against:</p>
              {samples.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-xs">
                  <FlaskConical className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                  No samples available. Go to Influent Entry to create one first.
                </div>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {samples.map(s => {
                    const active = selectedSampleId === s.id;
                    const params = s.params as Record<string, number> || {};
                    return (
                      <button
                        key={s.id}
                        onClick={() => setSelectedSampleId(s.id)}
                        className={`w-full text-left p-3 rounded-xl border transition-all ${
                          active ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-800/30 border-slate-800/50 hover:border-slate-600'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`text-xs font-bold ${active ? 'text-emerald-400' : 'text-slate-300'}`}>{s.sampleName}</p>
                            <p className="text-[10px] text-slate-500 mt-0.5">{s.reportNumber || `ID: ${s.id}`} · {s.standard}</p>
                          </div>
                          <div className="flex gap-1.5 flex-wrap justify-end">
                            {Object.entries(params).slice(0, 4).map(([k, v]) => (
                              <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">
                                {k.toUpperCase()}: {typeof v === 'number' ? v.toFixed(1) : v}
                              </span>
                            ))}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setStep(1)} className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-400 text-xs font-bold hover:bg-slate-800/50 flex items-center justify-center gap-1">
                  <ChevronRight className="w-3.5 h-3.5 rotate-180" /> Back
                </button>
                <button
                  onClick={runCompatCheck}
                  disabled={!selectedSampleId}
                  className="flex-1 py-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs font-bold hover:bg-emerald-500/30 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  Check Compatibility <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Equipment Compatibility Check */}
          {step === 3 && compatCheck && (
            <div className="space-y-4">
              <p className="text-xs text-slate-400">Checking equipment sequence compatibility and engineering logic:</p>

              {compatCheck.errors.length > 0 ? (
                <>
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30">
                    <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-rose-400">Blocking Issues Detected</p>
                      <p className="text-[10px] text-rose-400/70 mt-0.5">These sequence errors must be corrected before validation can proceed. Close this wizard, fix your equipment selection, then try again.</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {compatCheck.errors.map((msg, i) => (
                      <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-rose-500/5 border border-rose-500/20">
                        <XSquare className="w-3.5 h-3.5 text-rose-400 shrink-0 mt-0.5" />
                        <p className="text-[10px] text-rose-300">{msg}</p>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={onClose}
                    className="w-full py-2.5 rounded-xl bg-rose-500/10 text-rose-400 border border-rose-500/20 text-xs font-bold hover:bg-rose-500/20 flex items-center justify-center gap-2"
                  >
                    <X className="w-3.5 h-3.5" /> Close &amp; Fix Equipment Selection
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <p className="text-xs font-bold text-emerald-400">Equipment sequence compatible — no blocking issues</p>
                  </div>
                  {compatCheck.warnings.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-[10px] font-bold text-amber-400/70 uppercase tracking-wider">Engineering Warnings</p>
                      {compatCheck.warnings.map((msg, i) => (
                        <div key={i} className="flex items-start gap-2 p-2 rounded bg-amber-500/5 border border-amber-500/20">
                          <AlertCircle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" /><p className="text-[10px] text-amber-400">{msg}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {compatCheck.suggestions.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-[10px] font-bold text-sky-400/70 uppercase tracking-wider">Suggestions</p>
                      {compatCheck.suggestions.map((msg, i) => (
                        <div key={i} className="flex items-start gap-2 p-2 rounded bg-sky-500/5 border border-sky-500/20">
                          <Lightbulb className="w-3.5 h-3.5 text-sky-400 shrink-0 mt-0.5" /><p className="text-[10px] text-sky-400">{msg}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-3 pt-2">
                    <button onClick={() => setStep(2)} className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-400 text-xs font-bold hover:bg-slate-800/50 flex items-center justify-center gap-1">
                      <ChevronRight className="w-3.5 h-3.5 rotate-180" /> Back
                    </button>
                    <button
                      onClick={runAdequacyCheck}
                      className="flex-1 py-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs font-bold hover:bg-emerald-500/30 flex items-center justify-center gap-2"
                    >
                      <Play className="w-3.5 h-3.5" /> Run Adequacy Analysis
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 4: Validation Run */}
          {step === 4 && running && (
            <div className="py-12 flex flex-col items-center gap-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-2 border-emerald-500/20 border-t-emerald-500 animate-spin" />
                <ShieldCheck className="w-7 h-7 text-emerald-400 absolute inset-0 m-auto" />
              </div>
              <div className="text-center">
                <p className="text-sm font-bold text-slate-200">Running adequacy analysis...</p>
                <p className="text-[10px] text-slate-500 mt-1">Analysing {equipmentKeys.length} equipment units against sample parameters</p>
              </div>
              <div className="flex flex-col gap-1 w-full max-w-xs">
                {equipmentKeys.map((key, i) => {
                  const eq = EQUIPMENT.find(e => e.key === key);
                  return (
                    <motion.div
                      key={key}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.15 }}
                      className="flex items-center gap-2 text-[10px] text-slate-400"
                    >
                      <Loader2 className="w-3 h-3 animate-spin text-emerald-500" />
                      {eq?.label || key}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 5: Results + Save */}
          {step === 5 && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 text-center">
                  <p className="text-2xl font-black text-emerald-400">{passCount}</p>
                  <p className="text-[10px] text-emerald-400/70">Pass</p>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-center">
                  <p className="text-2xl font-black text-amber-400">{warnCount}</p>
                  <p className="text-[10px] text-amber-400/70">Warning</p>
                </div>
                <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-3 text-center">
                  <p className="text-2xl font-black text-rose-400">{failCount}</p>
                  <p className="text-[10px] text-rose-400/70">Fail</p>
                </div>
              </div>

              {failCount > 0 && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-500/5 border border-rose-500/20">
                  <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-rose-400">{failCount} equipment unit{failCount > 1 ? 's' : ''} inadequate for this influent. Consider revising the train before finalising the proposal.</p>
                </div>
              )}

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {results.map((r) => {
                  const IconC = r.adequate === 'pass' ? CheckSquare : r.adequate === 'warning' ? AlertCircle : XSquare;
                  const color = r.adequate === 'pass' ? 'text-emerald-400' : r.adequate === 'warning' ? 'text-amber-400' : 'text-rose-400';
                  const bg = r.adequate === 'pass' ? 'bg-emerald-500/5 border-emerald-500/20' : r.adequate === 'warning' ? 'bg-amber-500/5 border-amber-500/20' : 'bg-rose-500/5 border-rose-500/20';
                  return (
                    <div key={r.key} className={`p-3 rounded-lg border ${bg}`}>
                      <div className="flex items-start gap-2">
                        <IconC className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${color}`} />
                        <div>
                          <p className={`text-[11px] font-bold ${color}`}>{r.label}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">{r.message}</p>
                          {r.recommendation && (
                            <p className="text-[10px] text-sky-400 mt-1 flex items-start gap-1">
                              <Lightbulb className="w-3 h-3 shrink-0 mt-0.5" />{r.recommendation}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-400 text-xs font-bold hover:bg-slate-800/50">Close</button>
                <button
                  onClick={runComplianceSimulation}
                  className="flex-1 py-2.5 rounded-xl bg-violet-500/20 text-violet-400 border border-violet-500/30 text-xs font-bold hover:bg-violet-500/30 flex items-center justify-center gap-2"
                >
                  <BarChart3 className="w-3.5 h-3.5" /> Compliance Simulation →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 6: Compliance Simulation ────────────────────────────── */}
          {step === 6 && (
            <div className="space-y-4">
              {simRunning ? (
                /* Running animation */
                <div className="py-10 flex flex-col items-center gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full border-2 border-violet-500/20 border-t-violet-500 animate-spin" />
                    <BarChart3 className="w-7 h-7 text-violet-400 absolute inset-0 m-auto" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-slate-200">Running compliance simulation…</p>
                    <p className="text-[10px] text-slate-500 mt-1">Physics · Chemistry · Mass balance through {equipmentKeys.length} treatment stages</p>
                  </div>
                  <div className="flex flex-col gap-1 w-full max-w-sm mt-2">
                    {equipmentKeys.filter(k => k !== 'sludge').map((key, i) => (
                      <motion.div key={key} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.22 }}
                        className="flex items-center gap-2 text-[10px] text-slate-400 bg-slate-800/30 rounded-lg px-3 py-1.5">
                        <Activity className="w-3 h-3 animate-pulse text-violet-400" />
                        <span>{LABEL_MAP[key] ?? key}</span>
                        <ArrowRight className="w-2.5 h-2.5 text-slate-600 ml-auto" />
                        <span className="text-slate-600 font-mono">simulating…</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ) : simulation ? (
                /* Simulation Results */
                <div className="space-y-4 max-h-[65vh] overflow-y-auto scrollbar-thin pr-1">

                  {/* Standard selector */}
                  <div className="flex items-center gap-2 flex-wrap p-3 rounded-xl bg-violet-500/5 border border-violet-500/15">
                    <Globe className="w-3.5 h-3.5 text-violet-400 shrink-0" />
                    <span className="text-[10px] font-bold text-slate-400">Standard:</span>
                    {(['UK_EA','AU_EPA','SA_WATER','EU_UWWTD','US_EPA'] as StandardCode[]).map(code => (
                      <button key={code} onClick={() => { setSimStandard(code); runComplianceSimulation(); }}
                        className={`text-[10px] px-2 py-0.5 rounded-full border font-bold transition-all ${simStandard === code ? 'bg-violet-500 border-violet-500 text-white' : 'border-slate-700 text-slate-500 hover:border-violet-400 hover:text-violet-300'}`}>
                        {code.replace('_', ' ')}
                      </button>
                    ))}
                  </div>

                  {/* Overall compliance banner */}
                  <div className={`flex items-center gap-3 p-3 rounded-xl border ${simulation.overallCompliant ? 'bg-emerald-500/8 border-emerald-500/25' : 'bg-rose-500/8 border-rose-500/25'}`}>
                    {simulation.overallCompliant
                      ? <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                      : <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-bold ${simulation.overallCompliant ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {simulation.overallCompliant ? '✓ Full Compliance Achieved' : `✗ ${simulation.gaps.length} Parameter${simulation.gaps.length > 1 ? 's' : ''} Exceed ${simulation.standardName} Limits`}
                      </p>
                      <p className="text-[10px] text-slate-500 mt-0.5">{simulation.summary}</p>
                    </div>
                    <div className="shrink-0 text-center">
                      <div className="text-xl font-black" style={{ color: simulation.complianceScore >= 90 ? '#10b981' : simulation.complianceScore >= 70 ? '#f59e0b' : '#ef4444' }}>
                        {simulation.complianceScore}%
                      </div>
                      <div className="text-[9px] text-slate-500">Compliance</div>
                    </div>
                  </div>

                  {/* Stage-by-stage water quality evolution */}
                  <div className="rb-card p-3">
                    <div className="flex items-center gap-2 mb-3">
                      <Activity className="w-3.5 h-3.5 text-violet-400" />
                      <span className="text-xs font-bold text-slate-200">Treatment Train — Water Quality Evolution</span>
                    </div>
                    <div className="space-y-1">
                      {/* Header */}
                      <div className="grid grid-cols-[120px_1fr_1fr_1fr_1fr_1fr] gap-1 text-[9px] font-bold text-slate-500 uppercase tracking-wider px-2 pb-1 border-b border-slate-800/50">
                        <span>Stage</span><span>BOD₅</span><span>COD</span><span>TSS</span><span>NH₃-N</span><span>TP</span>
                      </div>
                      {/* Influent row */}
                      <div className="grid grid-cols-[120px_1fr_1fr_1fr_1fr_1fr] gap-1 px-2 py-1.5 rounded-lg bg-slate-800/40 text-[10px]">
                        <span className="font-bold text-sky-400">⬇ Influent</span>
                        {(['bod5','cod','tss','ammoniaN','totalPhosphorus'] as const).map(p => (
                          <span key={p} className="font-mono text-slate-300">{fmtWQParam(simulation.influent[p], p)}</span>
                        ))}
                      </div>
                      {/* Each stage */}
                      {simulation.stages.filter(s => s.equipmentKey !== 'sludge').map((stage, i) => {
                        const isExpanded = expandedStage === i;
                        const hasDosing = stage.dosing.length > 0;
                        const hasWarn = stage.warnings.length > 0;
                        return (
                          <div key={i}>
                            <button onClick={() => setExpandedStage(isExpanded ? null : i)}
                              className="w-full grid grid-cols-[120px_1fr_1fr_1fr_1fr_1fr] gap-1 px-2 py-1.5 rounded-lg hover:bg-slate-800/30 text-[10px] transition-all text-left">
                              <span className={`font-bold truncate ${hasWarn ? 'text-amber-400' : 'text-slate-300'}`}>
                                {hasWarn ? '⚠ ' : '↓ '}{stage.equipmentLabel}
                              </span>
                              {(['bod5','cod','tss','ammoniaN','totalPhosphorus'] as const).map(p => {
                                const inf = stage.influent[p] as number;
                                const eff = stage.effluent[p] as number;
                                const rem = inf > 0 ? ((inf - eff) / inf * 100) : 0;
                                const improved = rem > 2;
                                return (
                                  <span key={p} className={`font-mono ${improved ? 'text-emerald-400' : 'text-slate-400'}`}>
                                    {fmtWQParam(eff, p)}{improved && <span className="text-[8px] text-emerald-600 ml-0.5">↓{Math.round(rem)}%</span>}
                                  </span>
                                );
                              })}
                            </button>
                            {isExpanded && (
                              <div className="mx-2 mb-1 p-2.5 rounded-lg bg-slate-800/40 border border-slate-800/60 space-y-1.5">
                                {stage.engineeringNotes.map((n, j) => (
                                  <p key={j} className="text-[9px] text-slate-400 flex gap-1"><Atom className="w-2.5 h-2.5 text-violet-400 shrink-0 mt-px" />{n}</p>
                                ))}
                                {hasDosing && stage.dosing.map((d, j) => (
                                  <p key={j} className="text-[9px] text-amber-400 flex gap-1"><FlaskConical className="w-2.5 h-2.5 shrink-0 mt-px" /><strong>{d.chemical}</strong> {d.dose} — {d.purpose}</p>
                                ))}
                                {hasWarn && stage.warnings.map((w, j) => (
                                  <p key={j} className="text-[9px] text-rose-400 flex gap-1"><AlertTriangle className="w-2.5 h-2.5 shrink-0 mt-px" />{w}</p>
                                ))}
                                {stage.oxygenDemandKgd > 0 && (
                                  <p className="text-[9px] text-cyan-400 flex gap-1"><Wind className="w-2.5 h-2.5 shrink-0 mt-px" />O₂ demand: ~{(stage.oxygenDemandKgd * 1000).toFixed(0)} g O₂/m³</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* Final effluent row */}
                      <div className="grid grid-cols-[120px_1fr_1fr_1fr_1fr_1fr] gap-1 px-2 py-1.5 rounded-lg bg-emerald-500/5 border border-emerald-800/20 text-[10px]">
                        <span className="font-bold text-emerald-400">✓ Final Effluent</span>
                        {(['bod5','cod','tss','ammoniaN','totalPhosphorus'] as const).map(p => {
                          const limit = (simulation.compliance.find(c => c.param === p)?.limit) ?? 0;
                          const val = simulation.finalEffluent[p] as number;
                          const pass = limit <= 0 || val <= limit;
                          return (
                            <span key={p} className={`font-mono font-bold ${pass ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {fmtWQParam(val, p)}
                              {limit > 0 && <span className="text-[8px] ml-0.5 text-slate-600">/{fmtWQParam(limit, p)}</span>}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <p className="text-[9px] text-slate-600 mt-2 flex gap-1"><Info className="w-2.5 h-2.5 shrink-0 mt-px" />Click any stage row to expand engineering notes, dosing chemistry and warnings.</p>
                  </div>

                  {/* Compliance parameter grid */}
                  <div className="rb-card p-3">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="w-3.5 h-3.5 text-violet-400" />
                      <span className="text-xs font-bold text-slate-200">Parameter Compliance — {simulation.standardName}</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                      {simulation.compliance.map(r => (
                        <div key={r.param} className={`p-2 rounded-lg border text-[9px] ${r.pass ? 'bg-emerald-500/4 border-emerald-900/25' : r.status === 'critical' ? 'bg-red-500/10 border-red-700/40' : 'bg-red-500/5 border-red-800/25'}`}>
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="font-bold text-slate-400">{r.label}</span>
                            <span className={`text-[8px] font-bold px-1 rounded ${r.pass ? 'text-emerald-400' : r.status === 'critical' ? 'bg-red-600 text-white' : 'text-rose-400'}`}>
                              {r.pass ? 'PASS' : r.status === 'critical' ? 'CRIT' : 'FAIL'}
                            </span>
                          </div>
                          <div className="font-mono font-bold" style={{ color: r.color }}>{fmtWQParam(r.finalValue, r.param)}<span className="text-slate-600 font-normal"> / {fmtWQParam(r.limit, r.param)} {r.unit}</span></div>
                          <div className="text-slate-600">{r.pass ? `${Math.round(r.margin)}% headroom` : `${Math.round(-r.margin)}% over limit`}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Gap Analysis — only if gaps exist */}
                  {simulation.gaps.length > 0 && (
                    <div className="rb-card p-3">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
                        <span className="text-xs font-bold text-slate-200">Gap Analysis — Remediation Options</span>
                      </div>
                      <div className="space-y-3">
                        {simulation.gaps.map(gap => {
                          const isOpen = expandedGap === gap.param;
                          return (
                            <div key={gap.param} className="border border-rose-900/30 rounded-xl overflow-hidden">
                              <button onClick={() => setExpandedGap(isOpen ? null : gap.param)}
                                className="w-full flex items-center gap-2 p-2.5 bg-rose-500/5 hover:bg-rose-500/8 transition-all">
                                <XSquare className="w-3.5 h-3.5 text-rose-400 shrink-0" />
                                <div className="flex-1 text-left min-w-0">
                                  <span className="text-[11px] font-bold text-rose-400">{gap.label}</span>
                                  <span className="text-[10px] text-slate-400 ml-2">
                                    {fmtWQParam(gap.finalValue, gap.param)} → limit {fmtWQParam(gap.limit, gap.param)} {gap.unit}
                                    <span className="text-rose-400 ml-1 font-mono">+{gap.exceedancePct.toFixed(0)}%</span>
                                  </span>
                                </div>
                                <span className="text-[9px] text-slate-500 shrink-0">{gap.alternatives.length} options {isOpen ? '▲' : '▼'}</span>
                              </button>
                              {isOpen && (
                                <div className="p-2.5 space-y-2 bg-slate-900/40">
                                  <p className="text-[9px] text-slate-500">Requires additional <strong className="text-slate-300">{gap.requiredRemoval.toFixed(0)}% removal</strong> of {gap.label} beyond current train.</p>
                                  {gap.alternatives.map((alt, ai) => (
                                    <div key={ai} className={`p-2 rounded-lg border text-[9px] ${alt.achieves ? 'border-emerald-800/30 bg-emerald-500/3' : 'border-slate-800/50 bg-slate-800/20'}`}>
                                      <div className="flex items-start gap-1.5 mb-1">
                                        {alt.achieves ? <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0 mt-px" /> : <Minus className="w-3 h-3 text-slate-500 shrink-0 mt-px" />}
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-1 flex-wrap">
                                            <span className={`font-bold ${alt.achieves ? 'text-emerald-300' : 'text-slate-300'}`}>{alt.name}</span>
                                            <span className="text-[8px] px-1 py-px rounded bg-slate-800 text-slate-500">{alt.stage}</span>
                                            <span className={`text-[8px] px-1 py-px rounded ${alt.achieves ? 'bg-emerald-900/40 text-emerald-400' : 'bg-slate-800 text-slate-500'}`}>
                                              {alt.removalPct}% removal → {fmtWQParam(alt.projectedValue, gap.param)} {gap.unit}
                                            </span>
                                            <span className="text-[8px] text-slate-600">{CAPEX_LABELS[alt.capexBand]}</span>
                                          </div>
                                        </div>
                                      </div>
                                      <p className="text-slate-500 ml-4.5 pl-0.5"><span className="text-violet-400">Chemistry:</span> {alt.chemistry}</p>
                                      <p className="text-slate-500 ml-4.5 mt-0.5"><span className="text-sky-400">Design:</span> {alt.designNote}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Process summary */}
                  {simulation.totalO2Kgd > 0 && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="rb-card p-2.5 text-center">
                        <div className="text-[10px] text-slate-500 uppercase font-bold">O₂ Demand</div>
                        <div className="text-base font-bold text-cyan-400 mt-0.5">{(simulation.totalO2Kgd * 1000).toFixed(0)} <span className="text-xs text-slate-500">g/m³</span></div>
                      </div>
                      <div className="rb-card p-2.5 text-center">
                        <div className="text-[10px] text-slate-500 uppercase font-bold">Sludge Gen.</div>
                        <div className="text-base font-bold text-amber-400 mt-0.5">{(simulation.totalSludgeKgd * 1000).toFixed(0)} <span className="text-xs text-slate-500">g VSS/m³</span></div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {!simRunning && (
                <div className="flex gap-3 pt-2 border-t border-slate-800/40">
                  <button onClick={() => setStep(5)} className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-400 text-xs font-bold hover:bg-slate-800/50 flex items-center justify-center gap-1">
                    <ChevronRight className="w-3.5 h-3.5 rotate-180" /> Back
                  </button>
                  <button
                    onClick={() => setStep(7)}
                    className="flex-1 py-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs font-bold hover:bg-emerald-500/30 flex items-center justify-center gap-2"
                  >
                    <Save className="w-3.5 h-3.5" /> Proceed to Save →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Step 7: Save ─────────────────────────────────────────────── */}
          {step === 7 && (
            <div className="space-y-4">
              {simulation && (
                <div className={`flex items-center gap-3 p-3 rounded-xl border ${simulation.overallCompliant ? 'bg-emerald-500/8 border-emerald-500/25' : 'bg-amber-500/8 border-amber-500/25'}`}>
                  {simulation.overallCompliant
                    ? <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0" />
                    : <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />}
                  <div>
                    <p className={`text-xs font-bold ${simulation.overallCompliant ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {simulation.overallCompliant
                        ? `Compliance verified — ${simulation.standardName}`
                        : `${simulation.gaps.length} gap${simulation.gaps.length > 1 ? 's' : ''} identified — supplementary treatment recommended`}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">Compliance score: {simulation.complianceScore}% · {simulation.compliance.filter(c => c.pass).length}/{simulation.compliance.length} parameters passing</p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3 p-3 rounded-xl bg-sky-500/5 border border-sky-500/15">
                <Save className="w-4 h-4 text-sky-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-bold text-slate-200">Save Validated Process Train</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Assessment results, compliance simulation, and gap analysis will be saved with the train record.</p>
                </div>
              </div>
              {failCount > 0 && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-500/5 border border-rose-500/20">
                  <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-rose-400">{failCount} equipment unit{failCount > 1 ? 's' : ''} showed inadequacy warnings. Review before finalising proposal.</p>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setStep(6)} className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-400 text-xs font-bold hover:bg-slate-800/50 flex items-center justify-center gap-1">
                  <ChevronRight className="w-3.5 h-3.5 rotate-180" /> Back
                </button>
                <button
                  onClick={() => { onSaveResult(results, selectedSampleId, simulation ?? undefined); onClose(); }}
                  className="flex-1 py-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs font-bold hover:bg-emerald-500/30 flex items-center justify-center gap-2"
                >
                  <Save className="w-3.5 h-3.5" /> Save Process Train
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ─── Equipment Card with Status Dot ──────────────────────────────────────────
type EqStatus = 'configured' | 'in_progress' | 'not_configured' | 'validating';

function EquipmentCard({
  eq, isSelected, status, onClick
}: {
  eq: EquipmentItem;
  isSelected: boolean;
  status: EqStatus;
  onClick: () => void;
}) {
  const Icon = eq.icon;
  const colors = CAT_COLORS[eq.category];

  const dotClass = status === 'configured'
    ? 'bg-emerald-500'
    : status === 'in_progress'
    ? 'bg-amber-400 animate-pulse'
    : status === 'validating'
    ? 'bg-sky-400'
    : 'bg-slate-600';  // not yet used in any train — neutral grey instead of alarming red

  return (
    <button
      onClick={onClick}
      title={eq.description}
      className={`relative flex flex-col items-center gap-2 p-3 rounded-xl border transition-all w-[96px] group ${
        isSelected
          ? `${colors.bg} ${colors.border} shadow-[0_0_12px_rgba(255,255,255,0.04)]`
          : 'bg-rb-surface border-slate-800 hover:border-slate-600 hover:bg-slate-800/30'
      }`}
    >
      {/* Status dot — green: assessed, amber: in saved train, grey: unused */}
      <span
        className={`absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full border-2 border-rb-surface ${dotClass}`}
        title={
          status === 'configured'   ? 'Used in an assessed train' :
          status === 'in_progress'  ? 'Used in a saved train (draft/in-progress)' :
          status === 'validating'   ? 'Validating…' :
                                      'Not yet added to any train'
        }
      />

      {/* Blue rotating ring for 'validating' status */}
      {status === 'validating' && (
        <span className="absolute inset-0 rounded-xl border-2 border-sky-500/60 border-t-sky-400 animate-spin" style={{ animationDuration: '1.5s' }} />
      )}

      {/* Icon */}
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
        isSelected ? `${colors.bg} border ${colors.border}` : 'bg-slate-800/60 border border-slate-800/40'
      }`}>
        <Icon className={`w-6 h-6 transition-colors ${isSelected ? colors.text : 'text-slate-500 group-hover:text-slate-300'}`} />
      </div>

      <span className={`text-[10px] font-bold text-center leading-tight transition-colors ${
        isSelected ? colors.text : 'text-slate-400 group-hover:text-slate-300'
      }`}>
        {eq.shortLabel}
      </span>

      {isSelected && (
        <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-current opacity-60" style={{ color: 'inherit' }} />
      )}
    </button>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const UNDO_LIMIT = 50;

export default function ProcessTrainPage() {
  const [urlParams] = useSearchParams();
  const deepLinkTrainId   = urlParams.get('id')         ? Number(urlParams.get('id'))          : null;
  const deepLinkCustomerId = urlParams.get('customer_id') ? Number(urlParams.get('customer_id')) : null;
  const deepLinkEdit = urlParams.get('edit') === '1';

  // When set, the builder is EDITING an existing train — Save updates it in
  // place instead of creating a new one.
  const [editingTrainId, setEditingTrainId] = useState<number | null>(null);

  const [activeTab, setActiveTab] = useState<'generation' | 'validation'>('generation');
  const [selectedSampleId, setSelectedSampleId] = useState<number | null>(null);
  const [trainName, setTrainName] = useState('');
  const [trainDesc, setTrainDesc] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState<string[]>([]);
  const [savingTrain, setSavingTrain] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [validationTrain, setValidationTrain] = useState<any | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [samplesExpanded, setSamplesExpanded] = useState(false);

  // ─── Undo / Redo for equipment selection ───────────────────────────────────
  const undoStack = useRef<string[][]>([]);
  const redoStack = useRef<string[][]>([]);

  const pushUndo = useCallback((prev: string[]) => {
    undoStack.current = [...undoStack.current, prev].slice(-UNDO_LIMIT);
    redoStack.current = []; // clear redo on new action
  }, []);

  const handleUndo = useCallback(() => {
    if (undoStack.current.length === 0) return;
    const prev = undoStack.current[undoStack.current.length - 1];
    undoStack.current = undoStack.current.slice(0, -1);
    setSelectedEquipment(curr => {
      redoStack.current = [...redoStack.current, curr].slice(-UNDO_LIMIT);
      return prev;
    });
    toast.info('Undo equipment selection');
  }, []);

  const handleRedo = useCallback(() => {
    if (redoStack.current.length === 0) return;
    const next = redoStack.current[redoStack.current.length - 1];
    redoStack.current = redoStack.current.slice(0, -1);
    setSelectedEquipment(curr => {
      undoStack.current = [...undoStack.current, curr].slice(-UNDO_LIMIT);
      return next;
    });
    toast.info('Redo equipment selection');
  }, []);

  const { activeProposal, logActivity, updateEquipmentStatus, updateActiveProposal } = useProposal();
  const { currentCustomer, activeStandard } = useSession();
  const { isAdmin, user: authUser } = useAuth();

  // Customer-filtered trains toggle
  const [showAllTrains, setShowAllTrains] = useState(false);

  // Log page visit
  useEffect(() => {
    logActivity('page_visit', 'process-train', currentCustomer?.id, activeProposal?.id);
  }, []); // eslint-disable-line

  // ─── Auto-save every 30 s when a draft is being built ──────────────────────
  const autoSaveRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    autoSaveRef.current = setInterval(() => {
      if (trainName.trim() && selectedEquipment.length > 0) {
        // Persist draft to localStorage without creating a new train record
        try {
          localStorage.setItem('rb_train_draft_v1', JSON.stringify({
            trainName, trainDesc, selectedEquipment, selectedSampleId,
            savedAt: new Date().toISOString(),
          }));
          toast.info('Draft auto-saved', { id: 'autosave', duration: 2000 });
        } catch { /* ignore */ }
      }
    }, 30_000);
    return () => { if (autoSaveRef.current) clearInterval(autoSaveRef.current); };
  }, [trainName, trainDesc, selectedEquipment, selectedSampleId]);

  // Restore draft from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem('rb_train_draft_v1');
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft.trainName && !trainName) {
        setTrainName(draft.trainName);
        setTrainDesc(draft.trainDesc || '');
        setSelectedEquipment(draft.selectedEquipment || []);
        setSelectedSampleId(draft.selectedSampleId || null);
        toast.info('Draft restored from auto-save', { duration: 3000 });
      }
    } catch { /* ignore */ }
  }, []); // eslint-disable-line

  // ─── Keyboard shortcuts ────────────────────────────────────────────────────
  useEffect(() => {
    const onSave = () => {
      if (trainName.trim() && selectedEquipment.length > 0) handleSaveTrain();
    };
    window.addEventListener('rb:save', onSave);
    return () => window.removeEventListener('rb:save', onSave);
  }); // intentionally no deps — always reads latest state via closure

  // ─── Data loading ───
  const apiSamples = trpc.influent.mySamples.useQuery(undefined, {
    retry: 1,
    onError: (err) => { setApiError(err.message); },
  });
  const apiTrains = trpc.influent.myTrains.useQuery(undefined, {
    retry: 1,
    onError: (err) => { setApiError(err.message); },
  });

  const [samples, setSamples] = useState<any[]>([]);
  const [trains, setTrains] = useState<any[]>([]);

  useEffect(() => { setSamples(getAllLocalSamples()); }, []);
  useEffect(() => {
    const apiData = apiSamples.data || [];
    if (!apiData.length) return;
    setSamples(prev => {
      const merged = [...prev];
      apiData.forEach((s: any) => { if (!merged.find((x: any) => x.id === s.id)) merged.push(s); });
      return merged.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    });
  }, [apiSamples.data]);

  useEffect(() => {
    const currentUserId = authUser ? Number(authUser.id) : 0;
    const localTrains = getLocalProcessTrains();
    // Only show trains belonging to the current user (userId 0 means old local-only data — show to owner only)
    const ownTrains = currentUserId
      ? localTrains.filter(t => !t.userId || t.userId === currentUserId)
      : localTrains;
    setTrains(ownTrains);
  }, [authUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Merge tRPC trains (if available)
  useEffect(() => {
    const apiData = apiTrains.data || [];
    if (!apiData.length) return;
    setTrains(prev => {
      const merged = [...prev];
      apiData.forEach((t: any) => { if (!merged.find((x: any) => x.id === t.id)) merged.push(t); });
      return merged.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    });
  }, [apiTrains.data]);

  // PHP API fallback — loads trains from server when tRPC is unavailable
  useEffect(() => {
    const token = localStorage.getItem('rb_api_token');
    if (!token) return;
    phpApiListTrains().then((rows: any[]) => {
      if (!rows?.length) return;
      setTrains(prev => {
        const existIds = new Set(prev.map((t: any) => t.id));
        const fresh = rows
          .filter((t: any) => !existIds.has(t.id))
          .map((t: any) => ({
            id:               t.id,
            userId:           t.userId ?? t.user_id ?? 0,
            name:             t.name || 'Untitled Train',
            description:      t.description ?? null,
            sampleId:         t.sampleId ?? t.sample_id ?? null,
            customerId:       t.customerId ?? t.customer_id ?? null,
            proposalId:       t.proposalId ?? t.proposal_id ?? null,
            equipmentList:    Array.isArray(t.equipmentList) ? t.equipmentList
                              : (typeof t.equipmentList === 'string' ? JSON.parse(t.equipmentList || '[]') : []),
            status:           t.status || 'draft',
            assessmentResult: t.assessmentResult ?? null,
            createdAt:        t.createdAt || t.created_at || new Date().toISOString(),
            updatedAt:        t.updatedAt || t.updated_at || new Date().toISOString(),
            _local:           false,
          }));
        if (!fresh.length) return prev;
        return [...prev, ...fresh].sort((a: any, b: any) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      });
    }).catch(() => {});
  }, []);

  const refetchAll = () => { apiSamples.refetch(); apiTrains.refetch(); };

  // Load an existing train back into the builder (Generation tab) for editing.
  const loadTrainIntoBuilder = useCallback((t: any) => {
    const keys = ((t.equipmentList as any[]) || []).map(e => e?.key).filter(Boolean);
    setSelectedEquipment(keys);
    setTrainName(t.name || '');
    setTrainDesc(t.description || '');
    setSelectedSampleId(t.sampleId != null ? Number(t.sampleId) : null);
    setEditingTrainId(Number(t.id));
    setActiveTab('generation');
    toast(`Editing "${t.name}" — adjust the equipment / sample, then Save to update it.`, { icon: '✏️', duration: 5000 });
  }, []);

  // ─── Deep-link: ?id=N[&edit=1] — edit=1 loads the train into the builder for
  // editing; otherwise it opens the train's validation report. Fires once the
  // matching train is available (all loaders feed into `trains`).
  const deepLinkApplied = useRef(false);
  useEffect(() => {
    if (!deepLinkTrainId || deepLinkApplied.current) return;
    const t = trains.find((x: any) => Number(x.id) === deepLinkTrainId);
    if (!t) return;           // not loaded yet — wait for next update
    deepLinkApplied.current = true;
    if (deepLinkEdit) {
      loadTrainIntoBuilder(t);
      return;
    }
    if (t.sampleId) setSelectedSampleId(Number(t.sampleId));
    setValidationTrain(t);
    setActiveTab('validation');
    toast(`Opened: ${t.name}`, { description: 'Assessment against linked influent sample', duration: 4000 });
  }, [trains, samples, deepLinkTrainId, deepLinkEdit, loadTrainIntoBuilder]);

  // ─── tRPC mutations ───
  const saveTrainMutation = trpc.influent.saveTrain.useMutation({
    onSuccess: () => { toast.success('Process train synced to database'); refetchAll(); },
    onError: (err) => { setApiError(err.message); },
  });
  const deleteTrainMutation = trpc.influent.deleteTrain.useMutation({
    onSuccess: () => { toast.success('Process train deleted'); refetchAll(); },
    onError: (err) => { toast.error(`Delete failed: ${err.message}`); },
  });
  const updateTrainMutation = trpc.influent.updateTrain.useMutation({
    onSuccess: () => { refetchAll(); },
    onError: (err) => { console.warn('Update failed:', err.message); },
  });

  // ─── Equipment status derived from saved trains ───
  // green  = equipment is in an assessed (complete) train
  // amber  = equipment is in an in-progress OR draft train (saved but not fully assessed)
  // red    = equipment has never been added to any saved train
  const configuredKeys = useMemo(() => {
    const keys = new Set<string>();
    trains.filter(t => t.status === 'assessed').forEach(t => {
      (t.equipmentList as any[] || []).forEach((e: any) => keys.add(e.key));
    });
    return keys;
  }, [trains]);

  const inProgressKeys = useMemo(() => {
    const keys = new Set<string>();
    // Include in_progress AND draft — any train that exists counts as in-progress use
    trains.filter(t => t.status === 'in_progress' || t.status === 'draft').forEach(t => {
      (t.equipmentList as any[] || []).forEach((e: any) => keys.add(e.key));
    });
    return keys;
  }, [trains]);

  function getEqStatus(key: string): EqStatus {
    if (selectedEquipment.includes(key)) return 'in_progress';
    if (configuredKeys.has(key)) return 'configured';
    if (inProgressKeys.has(key)) return 'in_progress';
    return 'not_configured';
  }

  const toggleEquipment = (key: string) => {
    setSelectedEquipment(prev => {
      pushUndo(prev);
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      updateEquipmentStatus(key, next.includes(key) ? 'in_progress' : 'not_configured');
      return next;
    });
  };

  const moveEquipment = (key: string, direction: 'up' | 'down') => {
    setSelectedEquipment(prev => {
      const idx = prev.indexOf(key);
      if (idx === -1) return prev;
      if (direction === 'up' && idx === 0) return prev;
      if (direction === 'down' && idx === prev.length - 1) return prev;
      const arr = [...prev];
      const swap = direction === 'up' ? idx - 1 : idx + 1;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return arr;
    });
  };

  // ─── LIVE simulation: influent → each equipment → final effluent + compliance.
  // Recomputes whenever the influent sample, equipment chain, or active discharge
  // standard changes, so the builder shows live per-stage water quality and the
  // end-of-train compliance verdict without running the wizard.
  const liveSim = useMemo<TrainSimulation | null>(() => {
    if (!selectedSampleId || selectedEquipment.length === 0) return null;
    const sample = samples.find((s: any) => s.id === selectedSampleId);
    if (!sample) return null;
    try { return simulateTrain(sample.params || {}, selectedEquipment, ((activeStandard as StandardCode) || 'UK_EA')); }
    catch { return null; }
  }, [selectedSampleId, selectedEquipment, samples, activeStandard]);

  const handleSaveTrain = async () => {
    if (!trainName.trim()) { toast.error('Process train name is required'); return; }
    if (selectedEquipment.length === 0) { toast.error('Select at least one equipment'); return; }
    const v = validateTrainSequence(selectedEquipment);
    if (v.errors.length > 0) { toast.error(`Cannot save: ${v.errors[0]}`, { duration: 5000 }); return; }

    setSavingTrain(true);
    const equipmentList = selectedEquipment.map(key => {
      const eq = EQUIPMENT.find(e => e.key === key);
      return { key, label: eq?.label ?? key, category: eq?.category ?? 'other' };
    });

    const trainData = {
      name: trainName.trim(),
      description: trainDesc,
      sampleId: selectedSampleId || null,
      customerId: currentCustomer?.id ? parseInt(currentCustomer.id) : null,
      proposalId: activeProposal?.id || null,
      equipmentList,
      status: 'draft' as const,
    };

    let savedId: number;
    if (editingTrainId != null) {
      // EDIT: update the existing train in place (preserve its id).
      updateLocalProcessTrain(editingTrainId, trainData as any);
      setTrains(prev => prev.map(t => (t.id === editingTrainId ? { ...t, ...trainData, id: editingTrainId } : t)));
      savedId = editingTrainId;
    } else {
      const localTrain = saveLocalProcessTrain(trainData);
      setTrains(prev => [localTrain, ...prev]);
      savedId = localTrain.id;
    }
    selectedEquipment.forEach(k => updateEquipmentStatus(k, 'in_progress'));

    // Link train to active proposal — and generate a formal process description
    if (activeProposal?.id) {
      // Build a professionally formatted description of this treatment train
      const STAGE_LABELS: Record<string, string> = {
        pretreatment: 'Pre-Treatment',
        primary:      'Primary Treatment',
        secondary:    'Secondary Treatment',
        tertiary:     'Tertiary / Polishing',
        sludge:       'Sludge Management',
      };
      const byStage: Record<string, string[]> = {};
      equipmentList.forEach(e => {
        const stage = e.category || 'other';
        if (!byStage[stage]) byStage[stage] = [];
        byStage[stage].push(e.label);
      });
      const processSequence = equipmentList.map(e => e.label).join(' → ');
      const stageBreakdown = Object.entries(byStage)
        .map(([cat, items]) => `  • ${STAGE_LABELS[cat] || cat}: ${items.join(', ')}`)
        .join('\n');
      const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
      const formalBlock =
        `TREATMENT TRAIN: ${trainData.name}\n` +
        `Prepared: ${dateStr}\n\n` +
        `Process Sequence:\n${processSequence}\n\n` +
        `Treatment Stages:\n${stageBreakdown}` +
        (trainDesc ? `\n\nDesign Notes:\n${trainDesc}` : '');
      const existingDesc = activeProposal.description?.trim() || '';
      const combinedDesc = existingDesc
        ? `${existingDesc}\n\n${'─'.repeat(48)}\n\n${formalBlock}`
        : formalBlock;

      updateActiveProposal({
        processTrainIds: [...(activeProposal.processTrainIds || []), String(savedId)],
        description: combinedDesc,
      });
    }

    logActivity('train_saved', 'process-train', currentCustomer?.id, activeProposal?.id, { trainName: trainData.name });

    toast.success(
      editingTrainId != null
        ? `Process train "${trainData.name}" updated`
        : activeProposal
          ? `Process train saved & linked to ${activeProposal.proposalNumber}`
          : 'Process train saved locally'
    );
    try { await saveTrainMutation.mutateAsync(editingTrainId != null ? { ...trainData, id: editingTrainId } as any : trainData); } catch { /* local save succeeded */ }

    setTrainName(''); setTrainDesc(''); setSelectedEquipment([]); setSelectedSampleId(null);
    setEditingTrainId(null);
    setSavingTrain(false);
  };

  const handleDeleteTrain = (id: number, isLocal?: boolean) => {
    const train = trains.find(t => t.id === id);
    const standard = (currentCustomer as any)?.standard || (train as any)?.standard || 'UK_EA';
    const policy = getRetentionPolicy(standard);
    const trainCreatedAt = (train as any)?.createdAt;
    if (isWithinRetention(trainCreatedAt, policy)) {
      if (!isAdmin) {
        toast.error(retentionToastMessage(policy, trainCreatedAt));
        return;
      }
      if (!window.confirm(adminOverridePrompt(policy, trainCreatedAt, 'process train'))) return;
      deleteLocalProcessTrain(id);
      setTrains(prev => prev.filter(t => t.id !== id));
      if (!isLocal) deleteTrainMutation.mutate({ id });
      toast.warning('Process train deleted (admin override — within retention period)');
      return;
    }
    if (!confirm('Delete this process train?')) return;
    deleteLocalProcessTrain(id);
    setTrains(prev => prev.filter(t => t.id !== id));
    if (!isLocal) deleteTrainMutation.mutate({ id });
    toast.success('Process train deleted');
  };

  const handleAssessTrain = (train: any) => {
    const updates = { status: 'assessed' as const, assessmentResult: { assessedAt: new Date().toISOString() } };
    updateLocalProcessTrain(train.id, updates);
    setTrains(prev => prev.map(t => t.id === train.id ? { ...t, ...updates } : t));
    updateTrainMutation.mutate({ id: train.id, ...updates });
    (train.equipmentList as any[] || []).forEach((e: any) => updateEquipmentStatus(e.key, 'configured'));
    logActivity('train_assessed', 'process-train', currentCustomer?.id, activeProposal?.id, { trainName: train.name });
  };

  const handleSetInProgress = (train: any) => {
    const updates = { status: 'in_progress' as const };
    updateLocalProcessTrain(train.id, updates);
    setTrains(prev => prev.map(t => t.id === train.id ? { ...t, ...updates } : t));
    updateTrainMutation.mutate({ id: train.id, ...updates });
  };

  const handleValidationResult = (results: AdequacyResult[], sampleId: number | null, simulation?: TrainSimulation) => {
    const hasIssues = results.some(r => r.adequate !== 'pass');
    const msg = hasIssues
      ? `Validation complete — ${results.filter(r => r.adequate === 'fail').length} fail(s), ${results.filter(r => r.adequate === 'warning').length} warning(s)`
      : 'All equipment passes adequacy check ✓';
    toast[hasIssues ? 'warning' : 'success'](msg, { duration: 5000 });
    if (simulation) {
      if (simulation.overallCompliant) {
        toast.success(`Compliance simulation: ${simulation.complianceScore}% — ${simulation.standardName} limits achieved ✓`, { duration: 5000 });
      } else {
        toast.warning(`Compliance gap: ${simulation.gaps.length} parameter${simulation.gaps.length > 1 ? 's' : ''} exceed ${simulation.standardName} limits — review remediation options`, { duration: 7000 });
      }
    }
    logActivity('validation_run', 'process-train', currentCustomer?.id, activeProposal?.id, { pass: results.filter(r => r.adequate === 'pass').length, fail: results.filter(r => r.adequate === 'fail').length, complianceScore: simulation?.complianceScore });
  };

  const selectedSample = samples.find(s => s.id === selectedSampleId);
  const samplesLoading = apiSamples.isLoading && samples.length === 0;
  const trainsLoading = apiTrains.isLoading && trains.length === 0;
  const trainValidation = validateTrainSequence(selectedEquipment);

  // ─── Breadcrumb items ─────────────────────────────────────────────────────
  const breadcrumbs: BreadcrumbItem[] = [
    ...(currentCustomer ? [{ label: currentCustomer.company || currentCustomer.name, icon: Building2, to: '/customers', badgeColor: 'cyan' as const }] : []),
    ...(activeProposal ? [{ label: 'Proposal', icon: FileText, badge: activeProposal.proposalNumber, to: '/proposals', badgeColor: 'violet' as const }] : []),
    { label: 'Process Train', icon: Workflow },
  ];

  return (
    <div className="max-w-[1440px] mx-auto space-y-5 pb-24">
      {/* Breadcrumb */}
      <BreadcrumbNav items={breadcrumbs} />

      {/* Active Customer Banner */}
      {currentCustomer && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-cyan-500/20 bg-cyan-500/5">
          <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse shrink-0" />
          <Users className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
          <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider">Customer Context:</span>
          <span className="text-sm font-bold text-white">{currentCustomer.company}</span>
          {(currentCustomer as any).industryType && (
            <span className="text-[10px] text-slate-500">{(currentCustomer as any).industryType}</span>
          )}
          {(currentCustomer as any).standard && (
            <span className="text-[10px] px-1.5 py-0.5 rounded border border-cyan-500/20 bg-cyan-500/10 text-cyan-400 font-bold">
              {(currentCustomer as any).standard.replace('_', ' ')}
            </span>
          )}
          <span className="ml-auto text-[10px] text-slate-500">New trains will be linked to this customer</span>
        </div>
      )}

      <PageHeader
        icon={Workflow}
        iconColor="text-amber-400"
        title="Process Train Management"
        subtitle={`Build treatment trains · validate equipment selection · generate proposals${currentCustomer ? ` · ${currentCustomer.company || currentCustomer.name}` : ''}`}
        badge={activeProposal ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-violet-500/10 border border-violet-500/20 text-violet-400 text-[10px] font-bold">
            <FileText className="w-3 h-3" />Proposal Mode — {activeProposal.proposalNumber}
          </span>
        ) : undefined}
        actions={
          <div className="flex items-center gap-1.5">
            <button onClick={handleUndo} disabled={undoStack.current.length === 0} title="Undo (Ctrl+Z)"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-slate-800/50 text-slate-400 border border-slate-800/50 hover:bg-slate-800 hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
              <Undo2 className="w-3.5 h-3.5" />Undo
            </button>
            <button onClick={handleRedo} disabled={redoStack.current.length === 0} title="Redo (Ctrl+Y)"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-slate-800/50 text-slate-400 border border-slate-800/50 hover:bg-slate-800 hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
              <Redo2 className="w-3.5 h-3.5" />Redo
            </button>
            {apiError && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px]">
                <WifiOff className="w-3 h-3" />
                <span>Local mode</span>
                <button onClick={() => { setApiError(null); refetchAll(); }} className="ml-1 hover:text-amber-300">
                  <RefreshCw className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        {/* LEFT: sidebar */}
        <div className="xl:col-span-3 space-y-3">
          {/* Tab switcher */}
          <div className="p-2 space-y-1 rb-inner-card">
            <button
              onClick={() => setActiveTab('generation')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-bold transition-all ${
                activeTab === 'generation' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
              }`}
            >
              <Workflow className="w-4 h-4" /> Train Generation
            </button>
            <button
              onClick={() => setActiveTab('validation')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-bold transition-all ${
                activeTab === 'validation' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
              }`}
            >
              <ListChecks className="w-4 h-4" /> Saved Trains
            </button>
          </div>

          {/* Compact sample picker */}
          <div className="overflow-hidden rb-inner-card">
            <button
              onClick={() => setSamplesExpanded(v => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-xs font-bold text-slate-300 hover:bg-slate-800/30"
            >
              <div className="flex items-center gap-2">
                <FlaskConical className="w-3.5 h-3.5 text-sky-400" />
                <span>Influent Samples</span>
                {selectedSample && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 truncate max-w-[80px]">{selectedSample.sampleName}</span>}
              </div>
              {samplesExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
            </button>

            <AnimatePresence>
              {samplesExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden border-t border-slate-800/60"
                >
                  <div className="p-3 space-y-1 max-h-64 overflow-y-auto">
                    {samplesLoading ? (
                      <div className="flex items-center gap-2 text-[10px] text-slate-500 py-3 justify-center">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading...
                      </div>
                    ) : samples.length === 0 ? (
                      <p className="text-[10px] text-slate-500 text-center py-3">No samples. Go to Influent Entry.</p>
                    ) : (
                      <>
                        <button
                          onClick={() => setSelectedSampleId(null)}
                          className={`w-full text-left px-2.5 py-2 rounded-lg text-[10px] border transition-all ${
                            !selectedSampleId ? 'bg-slate-700/40 border-slate-600/50 text-slate-300' : 'border-transparent text-slate-500 hover:bg-slate-800/40'
                          }`}
                        >
                          None selected
                        </button>
                        {samples.map(s => {
                          const active = selectedSampleId === s.id;
                          return (
                            <button
                              key={s.id}
                              onClick={() => { setSelectedSampleId(active ? null : s.id); setSamplesExpanded(false); }}
                              className={`w-full text-left px-2.5 py-2 rounded-lg text-[10px] border transition-all ${
                                active ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' : 'border-transparent text-slate-400 hover:bg-slate-800/40 hover:text-slate-300'
                              }`}
                            >
                              <div className="flex items-center justify-between gap-1">
                                <span className="truncate font-medium">{s.sampleName}</span>
                                {active && <CheckCircle2 className="w-3 h-3 shrink-0" />}
                              </div>
                              <p className="text-[10px] text-slate-600 mt-0.5 truncate">{s.reportNumber || `ID: ${s.id}`}</p>
                            </button>
                          );
                        })}
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Selected sample summary (collapsed state) */}
            {!samplesExpanded && selectedSample && (
              <div className="px-3 pb-3 space-y-1">
                <div className="flex flex-wrap gap-1">
                  {Object.entries(selectedSample.params || {}).slice(0, 6).map(([k, v]) => (
                    <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">
                      {k.toUpperCase()}: {typeof v === 'number' ? (v as number).toFixed(1) : String(v)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Storage status */}
          <div className="p-3 space-y-2 rb-card">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Storage</p>
            <div className="flex items-center gap-2 text-[10px]">
              <Database className="w-3.5 h-3.5 text-emerald-400" /><span className="text-emerald-400">Local storage active</span>
            </div>
            <div className="flex items-center gap-2 text-[10px]">
              <Server className={`w-3.5 h-3.5 ${apiError ? 'text-amber-400' : 'text-emerald-400'}`} />
              <span className={apiError ? 'text-amber-400' : 'text-emerald-400'}>{apiError ? 'Sync pending' : 'Server sync active'}</span>
            </div>
            <button onClick={refetchAll} className="w-full mt-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] font-bold bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>

          {/* Legend */}
          <div className="p-3 space-y-2 rb-card">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Equipment Status</p>
            <div className="space-y-1.5">
              {[
                { dot: 'bg-emerald-500', label: 'Configured — in assessed train' },
                { dot: 'bg-amber-400 animate-pulse', label: 'In progress — selected / building' },
                { dot: 'bg-rose-500', label: 'Not configured' },
              ].map(({ dot, label }) => (
                <div key={label} className="flex items-center gap-2 text-[10px] text-slate-400">
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dot}`} />
                  {label}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: content */}
        <div className="xl:col-span-9 space-y-5">
          <AnimatePresence mode="wait">
            {/* ─── GENERATION TAB ─── */}
            {activeTab === 'generation' && (
              <motion.div key="generation" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }} className="space-y-4">
                {/* Name + description */}
                <div className="p-4 space-y-3 rb-inner-card">
                  {editingTrainId != null && (
                    <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-300">
                      <span className="flex items-center gap-1.5"><Cog className="w-3.5 h-3.5" /> Editing existing train #{editingTrainId} — Save will <strong>update</strong> it.</span>
                      <button onClick={() => { setEditingTrainId(null); setTrainName(''); setTrainDesc(''); setSelectedEquipment([]); setSelectedSampleId(null); }}
                        className="px-2 py-0.5 rounded border border-slate-600 text-slate-300 hover:text-white text-[10px]">Cancel edit</button>
                    </div>
                  )}
                  <h3 className="text-sm font-bold text-slate-200 flex items-center gap-2">
                    <Workflow className="w-4 h-4 text-amber-400" /> {editingTrainId != null ? 'Edit Process Train' : 'New Process Train'}
                    {activeProposal && (
                      <span className="text-[10px] text-violet-400 font-normal">→ attached to proposal {activeProposal.proposalNumber}</span>
                    )}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium text-slate-400">Train Name *</label>
                      <input value={trainName} onChange={e => setTrainName(e.target.value)} placeholder="e.g. Dairy Effluent Train A"
                        className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/40" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium text-slate-400">Description</label>
                      <input value={trainDesc} onChange={e => setTrainDesc(e.target.value)} placeholder="Optional description..."
                        className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/40" />
                    </div>
                  </div>
                </div>

                {/* Equipment selection with large icons */}
                <div className="p-4 space-y-4 rb-inner-card">
                  <h3 className="text-sm font-bold text-slate-200 flex items-center gap-2">
                    <Cog className="w-4 h-4 text-slate-400" /> Select Equipment
                    <span className="text-[10px] font-normal text-slate-500">— click to add/remove</span>
                  </h3>

                  {(['pretreatment', 'primary', 'secondary', 'tertiary', 'sludge'] as const).map(cat => {
                    const catEquip = EQUIPMENT.filter(e => e.category === cat);
                    const colors = CAT_COLORS[cat];
                    return (
                      <div key={cat}>
                        <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${colors.text}`}>{CAT_LABELS[cat]}</p>
                        <div className="flex flex-wrap gap-2">
                          {catEquip.map(eq => (
                            <EquipmentCard
                              key={eq.key}
                              eq={eq}
                              isSelected={selectedEquipment.includes(eq.key)}
                              status={getEqStatus(eq.key)}
                              onClick={() => toggleEquipment(eq.key)}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Selected sequence + validation */}
                {selectedEquipment.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="p-4 space-y-4 rb-inner-card">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-slate-200 flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-amber-400" /> Selected Sequence
                      </h3>
                      <span className="text-[10px] text-slate-500">Use ▲▼ to reorder</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {selectedEquipment.map((key, i) => {
                        const eq = EQUIPMENT.find(e => e.key === key)!;
                        const colors = CAT_COLORS[eq.category];
                        return (
                          <div key={key} className="flex items-center gap-2">
                            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ${colors.bg} ${colors.border}`}>
                              <span className={`text-[11px] font-bold ${colors.text}`}>{eq.shortLabel}</span>
                              <div className="flex flex-col ml-1">
                                <button onClick={() => moveEquipment(key, 'up')} disabled={i === 0} className="text-slate-500 hover:text-white disabled:opacity-20 leading-none"><ArrowUp className="w-2.5 h-2.5" /></button>
                                <button onClick={() => moveEquipment(key, 'down')} disabled={i === selectedEquipment.length - 1} className="text-slate-500 hover:text-white disabled:opacity-20 leading-none"><ArrowDownIcon className="w-2.5 h-2.5" /></button>
                              </div>
                            </div>
                            {i < selectedEquipment.length - 1 && <ChevronRight className="w-3 h-3 text-slate-600" />}
                          </div>
                        );
                      })}
                    </div>

                    {/* Validation alerts */}
                    {(trainValidation.errors.length > 0 || trainValidation.warnings.length > 0 || trainValidation.suggestions.length > 0) ? (
                      <div className="space-y-1.5 pt-2 border-t border-slate-800">
                        {trainValidation.errors.map((msg, i) => (
                          <div key={`e${i}`} className="flex items-start gap-2 p-2 rounded bg-rose-500/5 border border-rose-500/20">
                            <AlertCircle className="w-3.5 h-3.5 text-rose-400 shrink-0 mt-0.5" /><p className="text-[10px] text-rose-400">{msg}</p>
                          </div>
                        ))}
                        {trainValidation.warnings.map((msg, i) => (
                          <div key={`w${i}`} className="flex items-start gap-2 p-2 rounded bg-amber-500/5 border border-amber-500/20">
                            <AlertCircle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" /><p className="text-[10px] text-amber-400">{msg}</p>
                          </div>
                        ))}
                        {trainValidation.suggestions.map((msg, i) => (
                          <div key={`s${i}`} className="flex items-start gap-2 p-2 rounded bg-sky-500/5 border border-sky-500/20">
                            <Lightbulb className="w-3.5 h-3.5 text-sky-400 shrink-0 mt-0.5" /><p className="text-[10px] text-sky-400">{msg}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-2 rounded bg-emerald-500/5 border border-emerald-500/20">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /><p className="text-[10px] text-emerald-400 font-bold">Sequence scientifically valid</p>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex items-center gap-3 pt-2 flex-wrap">
                      <button onClick={handleSaveTrain} disabled={savingTrain || trainValidation.errors.length > 0}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 disabled:opacity-40 disabled:cursor-not-allowed">
                        <Save className="w-4 h-4" />{savingTrain ? 'Saving...' : editingTrainId != null ? 'Update Process Train' : 'Save Process Train'}
                      </button>
                      <button
                        onClick={() => {
                          logActivity('validation_start', 'process-train', currentCustomer?.id, activeProposal?.id);
                          setShowWizard(true);
                        }}
                        disabled={selectedEquipment.length === 0}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ShieldCheck className="w-4 h-4" /> Start Process Validation
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* ─── LIVE SIMULATION — influent → each stage → final + compliance ─── */}
                <div className="p-4 space-y-3 rb-inner-card">
                  <h3 className="text-sm font-bold text-slate-200 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-cyan-400" /> Live simulation — influent → effluent
                    <span className="ml-auto text-[10px] font-normal text-slate-500">
                      vs {(activeStandard as string) || 'UK_EA'} · updates as you build
                    </span>
                  </h3>

                  {!selectedSampleId ? (
                    <p className="text-[11px] text-slate-500">Select an influent sample above to calculate live water quality through each stage.</p>
                  ) : selectedEquipment.length === 0 ? (
                    <p className="text-[11px] text-slate-500">Add equipment to the train to see the stage-by-stage cascade.</p>
                  ) : !liveSim ? (
                    <p className="text-[11px] text-slate-500">Could not simulate — check the selected sample has water-quality data.</p>
                  ) : (() => {
                    const KEYS: Array<keyof typeof WQ_META> = ['cod', 'bod5', 'tss', 'ammoniaN', 'totalPhosphorus', 'oilGrease'] as any;
                    const active = KEYS.filter(k => (liveSim.influent as any)[k] != null && (liveSim.influent as any)[k] > 0);
                    return (
                      <div className="space-y-3">
                        {/* stage-by-stage cascade table */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-[11px]">
                            <thead className="border-b border-slate-700 text-slate-400">
                              <tr>
                                <th className="text-left py-1 px-2">Stage</th>
                                {active.map(k => <th key={k} className="text-right py-1 px-2">{(WQ_META as any)[k]?.label ?? k}</th>)}
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b border-slate-800/40 bg-slate-800/20">
                                <td className="py-1 px-2 font-bold text-amber-300">▶ Influent</td>
                                {active.map(k => <td key={k} className="text-right py-1 px-2 text-slate-300">{fmtWQParam((liveSim.influent as any)[k], k)}</td>)}
                              </tr>
                              {liveSim.stages.map((st, i) => (
                                <tr key={i} className="border-b border-slate-800/40">
                                  <td className="py-1 px-2 text-slate-200 truncate max-w-[160px]">{i + 1}. {st.equipmentLabel}</td>
                                  {active.map(k => {
                                    const v = (st.effluent as any)[k];
                                    const rem = (st.removals as any)[k];
                                    return (
                                      <td key={k} className="text-right py-1 px-2 text-slate-300">
                                        {fmtWQParam(v, k)}
                                        {rem > 0.5 && <span className="text-[8px] text-emerald-500 ml-0.5">↓{Math.round(rem)}%</span>}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                              <tr className="bg-slate-800/40 font-bold">
                                <td className="py-1 px-2 text-cyan-200">◀ Final effluent</td>
                                {active.map(k => <td key={k} className="text-right py-1 px-2 text-cyan-200">{fmtWQParam((liveSim.finalEffluent as any)[k], k)}</td>)}
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* end-of-train compliance vs active standard */}
                        <div className={`p-3 rounded-lg border ${liveSim.overallCompliant ? 'border-emerald-500/40 bg-emerald-900/10' : 'border-rose-500/40 bg-rose-900/10'}`}>
                          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                            <span className="text-xs font-bold flex items-center gap-1.5">
                              {liveSim.overallCompliant
                                ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                : <AlertCircle className="w-4 h-4 text-rose-400" />}
                              Compliance vs {liveSim.standardName}: <span className={liveSim.overallCompliant ? 'text-emerald-300' : 'text-rose-300'}>{liveSim.overallCompliant ? 'PASS' : 'FAIL'}</span>
                            </span>
                            <span className="text-[11px] text-slate-400">Score {liveSim.complianceScore}% · {liveSim.compliance.filter(c => c.pass).length}/{liveSim.compliance.length} params</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {liveSim.compliance.map(c => (
                              <span key={String(c.param)} className="text-[10px] px-1.5 py-0.5 rounded border font-mono"
                                style={{ color: c.color, borderColor: c.color + '55', background: c.color + '12' }}
                                title={`${c.label}: ${fmtWQParam(c.finalValue, c.param)} / ${fmtWQParam(c.limit, c.param)} ${c.unit}`}>
                                {c.label} {c.pass ? '✓' : '✕'}
                              </span>
                            ))}
                          </div>
                          {liveSim.gaps.length > 0 && (
                            <p className="text-[10px] text-rose-300 mt-2">
                              {liveSim.gaps.length} parameter(s) exceed the limit — open <strong>Start Process Validation</strong> for remediation options, or add a polishing stage.
                            </p>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-500">
                          Each stage's effluent is the next stage's influent (live cascade); the final effluent is checked against your active standard ({(activeStandard as string) || 'UK_EA'}). Chemical dosing, O₂ demand and sludge per stage are in the full report.
                        </p>
                      </div>
                    );
                  })()}
                </div>
              </motion.div>
            )}

            {/* ─── VALIDATION / SAVED TRAINS TAB ─── */}
            {activeTab === 'validation' && (
              <motion.div key="validation" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }} className="space-y-5">
                <div className="p-4 space-y-4 rb-inner-card">
                  <h3 className="text-sm font-bold text-slate-200 flex items-center gap-2">
                    <ListChecks className="w-4 h-4 text-emerald-400" /> Saved Process Trains
                    <span className="ml-auto text-[10px] font-normal text-slate-500">
                      {currentCustomer && !showAllTrains
                        ? `${trains.filter(t => String((t as any).customerId) === currentCustomer.id || (t as any).customerId === parseInt(currentCustomer.id)).length} for ${currentCustomer.company}`
                        : `${trains.length} total`}
                    </span>
                    {currentCustomer && (
                      <button onClick={() => setShowAllTrains(v => !v)}
                        className="ml-1 text-[10px] px-1.5 py-0.5 rounded border border-slate-700 text-slate-500 hover:text-slate-300 hover:border-slate-600 transition-all">
                        {showAllTrains ? 'Mine only' : 'Show all'}
                      </button>
                    )}
                  </h3>

                  {/* Customer filter notice */}
                  {currentCustomer && !showAllTrains && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-500/5 border border-cyan-500/15 text-[10px] text-cyan-400">
                      <Users className="w-3 h-3 shrink-0" />
                      Showing trains for <strong className="ml-0.5">{currentCustomer.company}</strong>
                    </div>
                  )}

                  {(() => {
                    const displayTrains = currentCustomer && !showAllTrains
                      ? trains.filter(t => {
                          const cid = (t as any).customerId;
                          return String(cid) === currentCustomer.id || cid === parseInt(currentCustomer.id);
                        })
                      : trains;
                    return trainsLoading ? (
                    <div className="flex items-center gap-2 text-xs text-slate-500 py-4 justify-center">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                    </div>
                  ) : displayTrains.length === 0 ? (
                    <div className="text-center py-8 text-xs text-slate-500">
                      <Workflow className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                      {currentCustomer && !showAllTrains
                        ? `No trains yet for ${currentCustomer.company}. Build one in the Generation tab.`
                        : 'No saved process trains. Switch to Generation tab to create one.'}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {displayTrains.map(train => {
                        const eqList = (train.equipmentList as any[]) || [];
                        const isLocal = train._local === true;
                        const statusConfig: Record<string, { text: string; bg: string; border: string; icon: React.ElementType }> = {
                          draft:       { text: 'text-slate-400', bg: 'bg-slate-800', border: 'border-slate-700', icon: Clock },
                          in_progress: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Loader2 },
                          assessed:    { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: ShieldCheck },
                        };
                        const sc = statusConfig[train.status] || statusConfig.draft;
                        const StatusIcon = sc.icon;

                        return (
                          <motion.div key={train.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            className="p-4 space-y-3 rb-card">
                            {/* Train header */}
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <div className="flex items-center gap-3 flex-wrap">
                                <div className={`px-2.5 py-1 rounded-lg border ${sc.bg} ${sc.border} flex items-center gap-1.5`}>
                                  <StatusIcon className={`w-3 h-3 ${sc.text} ${train.status === 'in_progress' ? 'animate-spin' : ''}`} />
                                  <span className={`text-[10px] font-bold uppercase ${sc.text}`}>{train.status.replace('_', ' ')}</span>
                                </div>
                                <h4 className="text-sm font-bold text-slate-200">{train.name}</h4>
                                {train.sampleId && (
                                  <span className="flex items-center gap-1 text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full">
                                    <FlaskConical className="w-3 h-3" /> Sample Linked
                                  </span>
                                )}
                                {isLocal && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-400 border border-sky-500/20">Local</span>}
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {train.status === 'draft' && (
                                  <button onClick={() => handleSetInProgress(train)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20">
                                    <Play className="w-3 h-3" /> Start
                                  </button>
                                )}
                                {train.status === 'in_progress' && (
                                  <button onClick={() => handleAssessTrain(train)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20">
                                    <ShieldCheck className="w-3 h-3" /> Mark Assessed
                                  </button>
                                )}
                                <button onClick={() => loadTrainIntoBuilder(train)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20">
                                  <Cog className="w-3 h-3" /> Edit
                                </button>
                                <button onClick={() => setValidationTrain(train)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20">
                                  <Microscope className="w-3 h-3" /> Full Report
                                </button>
                                <button onClick={() => window.print()} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-slate-700/50 text-slate-300 border border-slate-700 hover:bg-slate-700">
                                  <Printer className="w-3 h-3" /> Print
                                </button>
                                <button onClick={() => handleDeleteTrain(train.id, isLocal)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-rose-500/10 text-rose-400 border border-rose-500/20 hover:bg-rose-500/20">
                                  <Trash2 className="w-3 h-3" /> Delete
                                </button>
                              </div>
                            </div>

                            {train.description && <p className="text-[10px] text-slate-500">{train.description}</p>}

                            {/* Equipment icons with status dots */}
                            <div className="flex flex-wrap items-center gap-2 pt-1">
                              {eqList.map((eq: any, i: number) => {
                                const eqDef = EQUIPMENT.find(e => e.key === eq.key);
                                const IconC = eqDef?.icon || Cog;
                                const colors = CAT_COLORS[eq.category] || CAT_COLORS.pretreatment;
                                const dotColor = train.status === 'assessed' ? 'bg-emerald-500' : train.status === 'in_progress' ? 'bg-amber-400 animate-pulse' : 'bg-slate-600';
                                return (
                                  <div key={i} className="flex items-center gap-1.5">
                                    <div className={`relative flex flex-col items-center gap-1 p-2 rounded-lg border ${colors.bg} ${colors.border}`} title={eq.label}>
                                      <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full border border-rb-surface ${dotColor}`} />
                                      <IconC className={`w-5 h-5 ${colors.text}`} />
                                      <span className={`text-[10px] font-bold ${colors.text}`}>{eqDef?.shortLabel || eq.label}</span>
                                    </div>
                                    {i < eqList.length - 1 && <ArrowRight className="w-3 h-3 text-slate-400" />}
                                  </div>
                                );
                              })}
                            </div>

                            <div className="flex items-center justify-between text-[10px] text-slate-600">
                              <span>Created {new Date(train.createdAt).toLocaleDateString()}</span>
                              {train.assessmentResult && <span className="flex items-center gap-1 text-emerald-500"><CheckCircle2 className="w-3 h-3" /> Assessed</span>}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  );
                  })()}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Validation Wizard Modal */}
      {showWizard && (
        <ValidationWizard
          equipmentKeys={selectedEquipment}
          samples={samples}
          preSelectedSampleId={selectedSampleId}
          activeStandard={activeStandard as StandardCode}
          onClose={() => setShowWizard(false)}
          onSaveResult={handleValidationResult}
        />
      )}

      {/* Full Validation Report Modal */}
      {validationTrain && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 pt-10 overflow-y-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-4xl p-6 space-y-4 rb-section">
            <ProcessTrainValidationReport
              equipmentKeys={(validationTrain.equipmentList as any[]).map((e: any) => e.key)}
              sample={(() => { const s = samples.find((x: any) => x.id === validationTrain.sampleId); return s?.params || {}; })()}
              trainName={validationTrain.name}
              onClose={() => setValidationTrain(null)}
            />
          </motion.div>
        </div>
      )}

      {/* Sticky Action Bar — bottom-right */}
      {activeTab === 'generation' && selectedEquipment.length > 0 && (
        <StickyActionBar
          actions={[
            {
              label: savingTrain ? 'Saving…' : 'Save Train',
              icon: Save,
              onClick: handleSaveTrain,
              variant: 'primary',
              loading: savingTrain,
              disabled: !trainName.trim() || trainValidation.errors.length > 0,
              shortcut: 'Ctrl+S',
            },
            {
              label: 'Validate',
              icon: ShieldCheck,
              onClick: () => setShowWizard(true),
              variant: 'secondary',
            },
            {
              label: 'Print',
              icon: Printer,
              onClick: () => window.print(),
              variant: 'ghost',
              shortcut: 'Ctrl+P',
            },
          ]}
        />
      )}
    </div>
  );
}
